import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api.js";
import { Skeleton } from "@bongeunsa/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { I18nEditor, EMPTY_I18N } from "@/components/I18nEditor.js";
import { DefaultLanguagePicker } from "@/components/DefaultLanguagePicker.js";
import { SlidePanel } from "@/components/SlidePanel.js";
import { InlineCompletion } from "@/components/CompletionBadge.js";
import { useToast } from "@/components/Toast.js";
import { computeI18nCompletion } from "@/lib/i18nCompletion.js";
import type { Test, TestQuestion, TestChoice, TestResultType } from "@bongeunsa/db";
import type { Language } from "@bongeunsa/i18n";

const PUBLIC_BASE = import.meta.env.DEV ? "http://localhost:5173" : "https://bongeunsa.app";
const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$|^[a-z0-9]{3}$/;

type TestFull = {
  test: Test;
  questions: Array<TestQuestion & { choices: TestChoice[] }>;
  resultTypes: TestResultType[];
};

export function TestEditPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { show: showToast, ToastUI } = useToast();

  const sensors = useSensors(useSensor(PointerSensor));
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);
  const dropTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ["test-edit", id],
    queryFn: () => api.get<TestFull>(`/api/tests/${id}`),
  });

  function invalidate() { qc.invalidateQueries({ queryKey: ["test-edit", id] }); }

  const reorderQuestionsMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put(`/api/tests/${id}/questions/reorder`, { ordered_ids: orderedIds }),
    onSuccess: invalidate,
    onError: () => showToast("순서 변경에 실패했습니다.", "error"),
  });

  function handleQuestionsDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !data) return;
    const qs = data.questions;
    const oldIndex = qs.findIndex((q) => q.id === active.id);
    const newIndex = qs.findIndex((q) => q.id === over.id);
    const reordered = arrayMove(qs, oldIndex, newIndex);
    qc.setQueryData(["test-edit", id], (old: typeof data) =>
      old ? { ...old, questions: reordered } : old
    );
    clearTimeout(dropTimerRef.current);
    setJustDroppedId(active.id as string);
    dropTimerRef.current = setTimeout(() => setJustDroppedId(null), 400);
    reorderQuestionsMutation.mutate(reordered.map((q) => q.id));
  }
  function onSaved(msg = "저장되었습니다.") { invalidate(); showToast(msg); }
  function onDeleted(msg = "삭제되었습니다.") { invalidate(); showToast(msg); }
  function onError(msg = "오류가 발생했습니다.") { showToast(msg, "error"); }

  if (isLoading) return <div className="p-8 space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-32 w-full rounded-xl" /></div>;
  if (!data) return null;
  const { test, questions, resultTypes } = data;

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {ToastUI}
      {/* ── Left: editor ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-2xl">
          <div className="mb-6 flex items-center gap-3">
            <Link to="/tests" className="text-sm text-gray-400 hover:text-gray-600">← 목록</Link>
            <span className="text-gray-300">/</span>
            <h1 className="text-xl font-bold text-gray-900">{test.name}</h1>
            {test.is_active && <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">액티브</span>}
          </div>

          {/* 제목(name_i18n) + default language */}
          <NameI18nEditor testId={id!} test={test} onSuccess={invalidate} />

          {/* Slug editor */}
          <SlugEditor testId={id!} currentSlug={test.slug} onSuccess={invalidate} />

          {/* Questions */}
          <section className="mb-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">문항 ({questions.length})</h2>
              <AddQuestionButton testId={id!} orderIndex={questions.length} onSuccess={() => onSaved("문항이 추가되었습니다.")} />
            </div>
            {questions.length === 0 ? <EmptyState text="문항이 없습니다." /> : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionsDragEnd}>
                <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-4">
                    {questions.map((q, qi) => (
                      <SortableQuestionCard key={q.id} question={q} index={qi} testId={id!} resultTypes={resultTypes}
                        onSaved={() => onSaved()} onDeleted={() => onDeleted("문항이 삭제되었습니다.")} onError={onError}
                        isHighlighted={justDroppedId === q.id} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {/* Result types */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">결과 유형 ({resultTypes.length})</h2>
              <AddResultTypeButton testId={id!} onSuccess={() => onSaved("결과 유형이 추가되었습니다.")} />
            </div>
            {resultTypes.length === 0 ? <EmptyState text="결과 유형이 없습니다." /> : (
              <div className="space-y-3">
                {resultTypes.map((rt) => (
                  <ResultTypeCard key={rt.id} resultType={rt} testId={id!}
                    onSaved={() => onSaved()} onDeleted={() => onDeleted("결과 유형이 삭제되었습니다.")} onError={onError} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── Right: result types preview ──────────────────────── */}
      <div className="hidden lg:flex w-[300px] shrink-0 flex-col gap-3 border-l border-gray-100 bg-gray-50 overflow-y-auto p-5">
        <p className="text-xs font-semibold text-gray-400 shrink-0">결과 유형 미리보기</p>
        {resultTypes.length === 0 ? <p className="text-xs text-gray-300">결과 유형이 없습니다.</p> : (
          resultTypes.map((rt) => {
            let koText: Record<string, string> = {};
            try { const obj = JSON.parse(rt.i18n) as Record<Language, Record<string, string>>; koText = obj.ko ?? {}; } catch {}
            return (
              <div key={rt.id} className="rounded-xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
                {rt.image_url ? (
                  <img src={rt.image_url} alt={koText.title ?? ""} className="w-full h-28 object-cover" />
                ) : (
                  <div className="h-20 bg-brand-50 flex items-center justify-center"><span className="text-2xl">🏛️</span></div>
                )}
                <div className="px-3 py-2">
                  <p className="text-xs font-mono text-brand-500">{rt.slug}</p>
                  <p className="text-sm font-semibold text-gray-800">{koText.title ?? "(제목 없음)"}</p>
                  {koText.subtitle && <p className="text-xs text-gray-400">{koText.subtitle}</p>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Slug Editor ───────────────────────────────────────────────

function SlugEditor({ testId, currentSlug, onSuccess }: { testId: string; currentSlug: string; onSuccess: () => void }) {
  const [slug, setSlug] = useState(currentSlug);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: () => api.put(`/api/tests/${testId}`, { slug }),
    onSuccess,
  });

  useEffect(() => {
    if (slug === currentSlug) { setAvailable(null); return; }
    if (!SLUG_REGEX.test(slug)) { setAvailable(false); return; }
    clearTimeout(timerRef.current);
    setChecking(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get<{ available: boolean }>(`/api/tests/check-slug/${slug}?excludeId=${testId}`);
        setAvailable(res.available);
      } catch { setAvailable(false); }
      setChecking(false);
    }, 500);
    return () => clearTimeout(timerRef.current);
  }, [slug, currentSlug, testId]);

  const canSave = slug !== currentSlug && available === true && SLUG_REGEX.test(slug);

  return (
    <div className="mb-6 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <label className="mb-2 block text-xs font-semibold text-gray-500">슬러그 (공개 URL)</label>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 shrink-0">/t/</span>
        <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="who-am-i" className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-brand-400 font-mono" />
        {checking && <span className="text-xs text-gray-400">확인 중...</span>}
        {!checking && available === true && slug !== currentSlug && <span className="text-xs text-green-600">✓ 사용 가능</span>}
        {!checking && available === false && <span className="text-xs text-red-500">✗ {SLUG_REGEX.test(slug) ? "이미 사용 중" : "형식 오류"}</span>}
        <button onClick={() => mutation.mutate()} disabled={!canSave || mutation.isPending}
          className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40 hover:bg-brand-600">
          {mutation.isPending ? "저장 중..." : "저장"}
        </button>
      </div>
      <p className="mt-1.5 text-xs text-gray-400">
        공개 URL: <span className="font-medium">{PUBLIC_BASE}/t/{slug}</span>
        <button onClick={() => navigator.clipboard.writeText(`${PUBLIC_BASE}/t/${slug}`).catch(() => {})}
          className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] hover:bg-gray-200">복사</button>
      </p>
    </div>
  );
}

// ── Question Card ─────────────────────────────────────────────

function SortableQuestionCard(props: React.ComponentProps<typeof QuestionCard>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.question.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <QuestionCard {...props} isDragging={isDragging} dragHandle={
        <button {...attributes} {...listeners} className="mt-0.5 cursor-grab touch-none text-gray-300 hover:text-gray-500 active:cursor-grabbing shrink-0" aria-label="순서 변경">⠿</button>
      } />
    </div>
  );
}

function QuestionCard({ question, index, testId, resultTypes, onSaved, onDeleted, onError, dragHandle, isDragging, isHighlighted }: {
  question: TestQuestion & { choices: TestChoice[] };
  index: number;
  testId: string;
  resultTypes: TestResultType[];
  onSaved: () => void;
  onDeleted: () => void;
  onError: () => void;
  dragHandle?: React.ReactNode;
  isDragging?: boolean;
  isHighlighted?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);
  const [addingChoice, setAddingChoice] = useState(false);

  const koText = (() => { try { return (JSON.parse(question.i18n) as Record<Language, Record<string, string>>).ko ?? {}; } catch { return {}; } })();
  const updateMutation = useMutation({
    mutationFn: () => api.put(`/api/tests/${testId}/questions/${question.id}`, { i18n: draftI18n }),
    onSuccess: () => { setEditing(false); onSaved(); },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/tests/${testId}/questions/${question.id}`),
    onSuccess: () => { setDeleteOpen(false); onDeleted(); },
    onError,
  });

  return (
    <div className={`rounded-xl bg-white shadow-sm transition-shadow duration-300 ${isDragging ? "ring-2 ring-brand-300 shadow-lg" : isHighlighted ? "ring-2 ring-brand-400" : "ring-1 ring-black/5"}`}>
      <div className="flex items-start gap-3 p-4">
        {dragHandle}
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">{index + 1}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 line-clamp-2">{(koText as Record<string, string>).question ?? "(질문 없음)"}</p>
          {(koText as Record<string, string>).description && <p className="mt-0.5 text-xs text-gray-400 line-clamp-1">{(koText as Record<string, string>).description}</p>}
          <I18nCompletionBadge i18nStr={question.i18n} fields={["question", "description"]} />
        </div>
        <div className="flex gap-1">
          <button onClick={() => { try { setDraftI18n(JSON.parse(question.i18n) as typeof EMPTY_I18N); } catch { setDraftI18n(EMPTY_I18N); } setEditing(true); }}
            className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-50">편집</button>
          <button onClick={() => setDeleteOpen(true)} className="rounded px-2 py-1 text-xs text-red-400 hover:bg-red-50">삭제</button>
        </div>
      </div>

      {/* Choices */}
      <div className="border-t border-gray-50 px-4 pb-3">
        <div className="mt-2 space-y-1.5">
          {question.choices.map((ch, ci) => {
            const ckoText = (() => { try { return (JSON.parse(ch.i18n) as Record<Language, Record<string, string>>).ko ?? {}; } catch { return {}; } })();
            const score = (() => { try { return JSON.parse(ch.score) as Record<string, number>; } catch { return {}; } })();
            const scoreEntries = Object.entries(score).filter(([, v]) => v !== 0);
            return (
              <ChoiceRow key={ch.id} label={String.fromCharCode(65 + ci)} text={(ckoText as Record<string, string>).label ?? "(선택지 없음)"}
                scoreEntries={scoreEntries} testId={testId} questionId={question.id} choiceId={ch.id} resultTypes={resultTypes} onSuccess={onSaved} />
            );
          })}
        </div>
        {addingChoice ? (
          <AddChoiceForm testId={testId} questionId={question.id} orderIndex={question.choices.length} resultTypes={resultTypes}
            onSuccess={() => { setAddingChoice(false); onSaved(); }} onCancel={() => setAddingChoice(false)} />
        ) : (
          <button onClick={() => setAddingChoice(true)} className="mt-2 text-xs text-brand-500 hover:underline">+ 선택지 추가</button>
        )}
      </div>

      {editing && (
        <div className="border-t border-gray-100 p-4 space-y-3">
          <I18nEditor value={draftI18n} fields={[{ key: "question", label: "질문" }, { key: "description", label: "설명", multiline: true }]} onChange={setDraftI18n} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:underline">취소</button>
            <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog open={deleteOpen} title="문항 삭제" description="이 문항과 모든 선택지가 삭제됩니다." confirmLabel="삭제" danger
        onConfirm={() => deleteMutation.mutate()} onCancel={() => setDeleteOpen(false)} />
    </div>
  );
}

function AddQuestionButton({ testId, orderIndex, onSuccess }: { testId: string; orderIndex: number; onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);
  const mutation = useMutation({
    mutationFn: () => api.post(`/api/tests/${testId}/questions`, { order_index: orderIndex, i18n: draftI18n }),
    onSuccess: () => { setOpen(false); setDraftI18n(EMPTY_I18N); onSuccess(); },
  });
  return (
    <>
      <button onClick={() => setOpen(true)} className="rounded-xl bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600">+ 문항 추가</button>
      {open && <SlidePanel title="문항 추가" onClose={() => setOpen(false)} onSubmit={() => mutation.mutate()} loading={mutation.isPending}>
        <I18nEditor value={draftI18n} fields={[{ key: "question", label: "질문" }, { key: "description", label: "설명", multiline: true }]} onChange={setDraftI18n} />
      </SlidePanel>}
    </>
  );
}

function ChoiceRow({ label, text, scoreEntries, testId, questionId, choiceId, resultTypes, onSuccess }: {
  label: string; text: string; scoreEntries: [string, number][];
  testId: string; questionId: string; choiceId: string;
  resultTypes: TestResultType[]; onSuccess: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>(() => Object.fromEntries(scoreEntries));
  const mutation = useMutation({
    mutationFn: () => api.put(`/api/tests/${testId}/questions/${questionId}/choices/${choiceId}`, { score: scores }),
    onSuccess: () => { setEditing(false); onSuccess(); },
  });
  return (
    <div className="rounded-lg bg-gray-50 text-xs text-gray-600">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="font-semibold text-gray-400">{label}</span>
        <span className="flex-1 truncate">{text}</span>
        {scoreEntries.length > 0 ? (
          <div className="flex shrink-0 flex-wrap gap-1">
            {scoreEntries.map(([slug, val]) => (
              <span key={slug} className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">{slug}{val !== 1 ? ` ×${val}` : ""}</span>
            ))}
          </div>
        ) : <span className="shrink-0 text-[10px] text-gray-300">미설정</span>}
        <button onClick={() => { setScores(Object.fromEntries(scoreEntries)); setEditing(true); }} className="shrink-0 text-gray-400 hover:text-brand-500">✏</button>
        <button onClick={() => api.delete(`/api/tests/${testId}/questions/${questionId}/choices/${choiceId}`).then(onSuccess)}
          className="text-red-300 hover:text-red-500" aria-label="선택지 삭제">✕</button>
      </div>
      {editing && (
        <div className="border-t border-gray-100 px-3 pb-3 pt-2 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">결과 유형별 가중치</p>
          {resultTypes.length === 0 ? <p className="text-[10px] text-gray-300">결과 유형을 먼저 추가하세요.</p> : (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {resultTypes.map((rt) => (
                <label key={rt.id} className="flex items-center gap-2">
                  <span className="w-20 truncate text-[10px] text-gray-600">{rt.slug}</span>
                  <input type="number" min={0} max={9} value={scores[rt.slug] ?? 0}
                    onChange={(e) => { const v = parseInt(e.target.value) || 0; setScores((prev) => { const next = { ...prev }; if (v === 0) delete next[rt.slug]; else next[rt.slug] = v; return next; }); }}
                    className="w-12 rounded border border-gray-200 px-1.5 py-0.5 text-center text-xs outline-none focus:border-brand-400" />
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setEditing(false)} className="text-[10px] text-gray-400 hover:underline">취소</button>
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
              className="rounded bg-brand-500 px-2 py-1 text-[10px] font-semibold text-white disabled:opacity-50">
              {mutation.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddChoiceForm({ testId, questionId, orderIndex, resultTypes, onSuccess, onCancel }: {
  testId: string; questionId: string; orderIndex: number;
  resultTypes: TestResultType[]; onSuccess: () => void; onCancel: () => void;
}) {
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);
  const [scores, setScores] = useState<Record<string, number>>({});
  const mutation = useMutation({
    mutationFn: () => api.post(`/api/tests/${testId}/questions/${questionId}/choices`, { order_index: orderIndex, score: scores, i18n: draftI18n }),
    onSuccess,
  });
  return (
    <div className="mt-2 rounded-xl border border-brand-200 bg-brand-50/30 p-3 space-y-2">
      <I18nEditor value={draftI18n} fields={[{ key: "label", label: "선택지 텍스트" }]} onChange={setDraftI18n} />
      {resultTypes.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">결과 유형별 가중치 (0 = 미선택)</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {resultTypes.map((rt) => (
              <label key={rt.id} className="flex items-center gap-2">
                <span className="w-20 truncate text-[10px] text-gray-600">{rt.slug}</span>
                <input type="number" min={0} max={9} value={scores[rt.slug] ?? 0}
                  onChange={(e) => { const v = parseInt(e.target.value) || 0; setScores((prev) => { const next = { ...prev }; if (v === 0) delete next[rt.slug]; else next[rt.slug] = v; return next; }); }}
                  className="w-12 rounded border border-gray-200 px-1.5 py-0.5 text-center text-xs outline-none focus:border-brand-400" />
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-xs text-gray-400 hover:underline">취소</button>
        <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
          className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">추가</button>
      </div>
    </div>
  );
}

// ── Result Type Card ──────────────────────────────────────────

function ResultTypeCard({ resultType, testId, onSaved, onDeleted, onError }: {
  resultType: TestResultType; testId: string;
  onSaved: () => void; onDeleted: () => void; onError: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);
  const [draftSlug, setDraftSlug] = useState(resultType.slug);
  const [draftImageUrl, setDraftImageUrl] = useState(resultType.image_url ?? "");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const koText = (() => { try { return (JSON.parse(resultType.i18n) as Record<Language, Record<string, string>>).ko ?? {}; } catch { return {}; } })();

  const updateMutation = useMutation({
    mutationFn: () => api.put(`/api/tests/${testId}/result-types/${resultType.id}`, { slug: draftSlug, i18n: draftI18n, image_url: draftImageUrl || null }),
    onSuccess: () => { setEditing(false); onSaved(); },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/tests/${testId}/result-types/${resultType.id}`),
    onSuccess: () => { setDeleteOpen(false); onDeleted(); },
    onError,
  });

  async function handleImageFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const res = await api.postForm<{ url: string }>("/api/admin/upload", (() => { const f = new FormData(); f.append("file", file); return f; })()); setDraftImageUrl(res.url); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  return (
    <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/5">
      <div className="flex items-center gap-3 px-4 py-3">
        {resultType.image_url && <img src={resultType.image_url} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />}
        <span className="rounded bg-brand-100 px-2 py-0.5 text-xs font-mono font-semibold text-brand-700">{resultType.slug}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{(koText as Record<string, string>).title ?? "(제목 없음)"}</p>
          <I18nCompletionBadge i18nStr={resultType.i18n} fields={["title", "subtitle", "desc", "temple_note"]} />
        </div>
        <button onClick={() => { try { setDraftI18n(JSON.parse(resultType.i18n) as typeof EMPTY_I18N); } catch { setDraftI18n(EMPTY_I18N); } setDraftSlug(resultType.slug); setDraftImageUrl(resultType.image_url ?? ""); setEditing(!editing); }}
          className="text-xs text-gray-500 hover:underline">{editing ? "닫기" : "편집"}</button>
        <button onClick={() => setDeleteOpen(true)} className="text-xs text-red-400 hover:underline">삭제</button>
      </div>
      {editing && (
        <div className="border-t border-gray-50 p-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">슬러그 (결과 URL에 사용)</label>
            <input value={draftSlug} onChange={(e) => setDraftSlug(e.target.value.toLowerCase())}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono outline-none focus:border-brand-400" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">결과 이미지</label>
            {draftImageUrl && <img src={draftImageUrl} alt="" className="mb-2 h-28 w-full rounded-lg object-cover ring-1 ring-black/10" />}
            <div className="flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="rounded-lg border border-dashed border-brand-400 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50">
                {uploading ? "업로드 중..." : "파일 업로드"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
            </div>
            <input type="url" value={draftImageUrl} onChange={(e) => setDraftImageUrl(e.target.value)} placeholder="https://..."
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-brand-400" />
          </div>
          <I18nEditor value={draftI18n} fields={[{ key: "title", label: "제목 (예: 사리불)" }, { key: "subtitle", label: "부제 (예: 지혜 제일)" }, { key: "desc", label: "설명", multiline: true }, { key: "temple_note", label: "전각 메모", multiline: true }]} onChange={setDraftI18n} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:underline">취소</button>
            <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog open={deleteOpen} title="결과 유형 삭제" description={`"${(koText as Record<string, string>).title}" 결과 유형을 삭제할까요?`}
        confirmLabel="삭제" danger onConfirm={() => deleteMutation.mutate()} onCancel={() => setDeleteOpen(false)} />
    </div>
  );
}

function AddResultTypeButton({ testId, onSuccess }: { testId: string; onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);
  const mutation = useMutation({
    mutationFn: () => api.post(`/api/tests/${testId}/result-types`, { slug, i18n: draftI18n }),
    onSuccess: () => { setOpen(false); setSlug(""); setDraftI18n(EMPTY_I18N); onSuccess(); },
  });
  return (
    <>
      <button onClick={() => setOpen(true)} className="rounded-xl bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600">+ 결과 유형 추가</button>
      {open && <SlidePanel title="결과 유형 추가" onClose={() => setOpen(false)} onSubmit={() => mutation.mutate()} loading={mutation.isPending}>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">슬러그 (URL + score key로 사용)</label>
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="예: sariputra"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono outline-none focus:border-brand-400" />
        </div>
        <I18nEditor value={draftI18n} fields={[{ key: "title", label: "제목" }, { key: "subtitle", label: "부제" }, { key: "desc", label: "설명", multiline: true }]} onChange={setDraftI18n} />
      </SlidePanel>}
    </>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-black/5"><p className="text-sm text-gray-400">{text}</p></div>;
}

function I18nCompletionBadge({ i18nStr, fields }: { i18nStr: string; fields: string[] }) {
  const { overall } = computeI18nCompletion(i18nStr, fields);
  return <InlineCompletion pct={overall} />;
}

function NameI18nEditor({ testId, test, onSuccess }: { testId: string; test: Test; onSuccess: () => void }) {
  const [nameI18n, setNameI18n] = useState<Record<Language, Record<string, string>>>(() => {
    try { return JSON.parse(test.name_i18n) as Record<Language, Record<string, string>>; } catch { return EMPTY_I18N; }
  });
  const [defaultLang, setDefaultLang] = useState<Language>(test.default_language as Language ?? "ko");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const isDirty = JSON.stringify(nameI18n) !== test.name_i18n || defaultLang !== (test.default_language ?? "ko");

  const mutation = useMutation({
    mutationFn: () => {
      const title = (nameI18n[defaultLang]?.title ?? "").trim();
      if (!title) { setError(`기본 언어(${defaultLang})의 제목을 입력하세요.`); return Promise.reject(); }
      setError("");
      return api.put(`/api/tests/${testId}`, { name_i18n: JSON.stringify(nameI18n), default_language: defaultLang });
    },
    onSuccess: () => { setOpen(false); onSuccess(); },
  });

  return (
    <div className="mb-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500">검사 제목 (다국어)</p>
          <p className="mt-0.5 text-sm font-medium text-gray-900 truncate">{(nameI18n["ko"]?.title ?? test.name) || test.name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isDirty && (
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending} type="button"
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
              {mutation.isPending ? "저장 중..." : "저장"}
            </button>
          )}
          <button onClick={() => setOpen(!open)} type="button"
            className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
            <span>{open ? "접기" : "다국어 편집"}</span>
            <svg className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <DefaultLanguagePicker value={defaultLang} onChange={setDefaultLang} />
          <I18nEditor value={nameI18n} fields={[{ key: "title", label: "제목" }]} onChange={setNameI18n} defaultLang={defaultLang} requiredFields={["title"]} />
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end">
            <button onClick={() => setOpen(false)} type="button" className="text-xs text-gray-500 hover:underline">닫기</button>
          </div>
        </div>
      )}
    </div>
  );
}
